import zxcvbn from 'zxcvbn';

// Mapping of feedback messages from English to Japanese
const feedbackTranslations = {
    // Warnings
    'Straight rows of keys are easy to guess': 'キーボード上で隣り合った文字の1列はパスワードに適していません。',
    'Short keyboard patterns are easy to guess': 'キーボード上で隣り合った文字の1列はパスワードに適していません。',
    'Repeats like "aaa" are easy to guess': '同じ文字列の繰り返しは推測されやすくパスワードに適していません。',
    'Repeats like "abcabcabc" are only slightly harder to guess than "abc"': '同じ文字列の繰り返しは推測されやすくパスワードに適していません。',
    'Sequences like abc or 6543 are easy to guess': '連続する文字や数字は推測されやすくパスワードに適していません。',
    'Recent years are easy to guess': '年はパスワードに適していません。',
    'Dates are often easy to guess': '日付はパスワードに適していません。',
    'This is a top-10 common password': 'パスワードとしてよく使われる文字列です。',
    'This is a top-100 common password': 'パスワードとしてよく使われる文字列です。',
    'This is a very common password': 'パスワードとしてよく使われる文字列です。',
    'This is similar to a commonly used password': 'パスワードとしてよく使われる文字列です。',
    'A word by itself is easy to guess': 'パスワードとしてよく使われる文字列です。',
    'Names and surnames by themselves are easy to guess': 'パスワードに人名を使うのはふさわしくありません。',
    'Common names and surnames are easy to guess': 'パスワードに人名を使うのはふさわしくありません。',

    // Suggestions
    'Add another word or two. Uncommon words are better.': '英数字や記号をいくつか追加してください。追加する文字列は、よく使われる語句ではないものが適しています。',
    'Avoid sequences': '文字や数字が連続しないようにしてください。',
    'Avoid recent years': '最近の年号は避けてください。',
    'Avoid years that are associated with you': '最近の年やあなたに関連する年を避けるようにしてください。',
    'Avoid dates and years that are associated with you': '日付ではないパスワードに変更してください。',
    'Avoid repeated words and characters': '文字列を繰り返さないようにしてください。',
    'Capitalization doesn\'t help very much': '大文字にすることで、ある程度効果があります。',
    'All-uppercase is almost as easy to guess as all-lowercase': '全て大文字のパスワードは、全て小文字のパスワードと同じくらい推測されやすいです。',
    'Use a few words, avoid common phrases': '一般的なフレーズを避けるため、いくつかの単語を使用してください。',
    'No need for symbols, digits, or uppercase letters': '記号、数字、または大文字である必要はありません。',
    'Reversed words aren\'t much harder to guess': '文字を逆並びにすることや記号に置き換えることは適切ではありません。',
    'Predictable substitutions like \'@\' instead of \'a\' don\'t help very much': '文字を逆並びにすることや記号に置き換えることは適切ではありません。',
    'Use a longer keyboard pattern with more turns': '英数字や記号を途中にいくつか追加してください。',
};

// List of feedback messages we want to exclude from splitting
const noSplitMessages = [
    'Add another word or two. Uncommon words are better.'
];

// List of feedback messages we want to exclude
const excludedFeedback = [
    'Use a few words, avoid common phrases',
    'Avoid recent years',
    'Avoid years that are associated with you',
    'No need for symbols, digits, or uppercase letters',
    'Capitalization doesn\'t help very much',
    'All-uppercase is almost as easy to guess as all-lowercase',
    'Use a longer keyboard pattern with more turns'
];

// Function to translate feedback messages
function translateFeedback(feedback) {
    // return feedback.flatMap(function(message) {
    return feedback
        .filter(message => !excludedFeedback.includes(message)) // Filter out excluded messages
        .flatMap(function (message) {

            // Translate feedback
            let translatedMessage = feedbackTranslations[message] || message;
            // Skip empty translations or unwanted messages
            if (!translatedMessage) {
                return [];
            }
            // Avoid splitting specific messages with "。"
            if (noSplitMessages.includes(message)) {
                return [translatedMessage]; // Return the message as is
            }
            // Split the message into separate parts if it contains "。"
            return translatedMessage.split("。").map(function (part, index, array) {
                // Add "。" back to each part except the last one and filter out empty parts
                return index < array.length - 1 ? part + "。" : part;
            }).filter(part => part.trim() !== ''); // Filter out empty parts
        });
}

// Function to check password strength
export function checkPasswordStrength(password) {
    if (!password) return {
        class: '',
        message: '',
        warning: '',
        suggestions: [],
        score: 0,
        isPasswordWeak: false,
        isPasswordAcceptable: false,
    };

    const result = zxcvbn(password);
    const strengthClass = getStrengthClass(result.score);
    const strengthMessage = getStrengthMessage(result.score, password.length);

    const warningMessage = result.feedback.warning
        ? feedbackTranslations[result.feedback.warning] || result.feedback.warning
        : '';

    const suggestionsMessage = result.feedback.suggestions ? translateFeedback(result.feedback.suggestions) : [];
    const feedbackMessage = `${strengthMessage}`;

    const isPasswordWeak = strengthClass === 'weak';
    const isPasswordAcceptable = strengthClass === 'acceptable';

    return {
        class: strengthClass,
        message: feedbackMessage,
        warning: warningMessage,
        suggestions: suggestionsMessage,
        score: result.score,
        isPasswordWeak: isPasswordWeak,
        isPasswordAcceptable: isPasswordAcceptable && !isPasswordWeak,
    };
}

// Main function to get the strength class of the password
export function getStrengthClass(score) {
    switch (score) {
        case 0:
            return 'weak';
        case 1:
            return 'acceptable';
        case 2:
            return 'fair';
        case 3:
            return 'strong';
        case 4:
            return 'very-strong';
        default:
            return '';
    }
}

// Function to get the strength message of the password
export function getStrengthMessage(score, length) {
    if (length < 8 || length > 64) {
        return '';
    }
    switch (score) {
        case 0:
            return 'パスワードの強度：弱い';
        case 1:
            return 'パスワードの強度：やや弱い';
        case 2:
            return 'パスワードの強度：普通';
        case 3:
            return 'パスワードの強度：強い';
        case 4:
            return 'パスワードの強度：とても強い';
        default:
            return '';
    }
}

export const getSegmentClass = (index, passwordLength, passwordStrengthScore) => {
    if (passwordLength < 8 || passwordLength > 64) {
        return '';
    }

    switch (passwordStrengthScore) {
        case 0:
            return index === 1 ? 'weak' : '';
        case 1:
            return index <= 2 ? 'acceptable' : '';
        case 2:
            return index <= 3 ? 'fair' : '';
        case 3:
            return index <= 4 ? 'strong' : '';
        case 4:
            return 'very-strong';
        default:
            return '';
    }
};
